<template>
  <div>
    <!-- Mobile sidebar overlay -->
    <div 
      v-if="isOpen" 
      class="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
      @click="closeSidebar"
    ></div>

    <!-- Sidebar -->
    <div 
      :class="[
        'fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-300 transform transition-transform duration-300 ease-in-out lg:translate-x-0 flex flex-col h-screen',
        isOpen ? 'translate-x-0' : '-translate-x-full'
      ]"
    >
      <!-- Sidebar Header -->
      <div class="flex items-center justify-between h-16 px-6 border-b border-gray-300 flex-shrink-0">
        <div class="flex items-center">
          <slot name="header">
            <h1 class="text-xl font-bold text-brand-blue">{{ title }}</h1>
          </slot>
        </div>
        <button 
          @click="closeSidebar"
          class="lg:hidden text-emerald-600 hover:text-emerald-700"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Sidebar Navigation -->
      <nav class="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
        <slot name="navigation">
          <!-- Default navigation items -->
          <NuxtLink 
            v-for="item in navigationItems"
            :key="item.path"
            :to="item.path"
            class="flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors"
            :class="[
              $route.path === item.path || $route.path.startsWith(item.path + '/')
                ? 'bg-brand-blue/10 text-brand-blue' 
                : 'text-gray-500 hover:bg-brand-blue/5 hover:text-brand-blue'
            ]"
          >
            <component 
              :is="item.icon" 
              class="w-5 h-5 mr-3"
              v-if="item.icon"
            />
            {{ item.label }}
          </NuxtLink>
        </slot>
        
        <!-- Logout Menu Item -->
        <div class="pt-4 border-t border-gray-200 mt-4">
          <button 
            @click="handleLogout"
            class="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-500 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
            v-if="showLogoutButton"
          >
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Logout
          </button>
        </div>
      </nav>

      <!-- User Profile Section -->
      <div v-if="showUserProfile" class="p-4 border-t border-gray-300 flex-shrink-0">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <img src="/profile.png" alt="Profile" class="w-10 h-10 rounded-full object-cover shadow-sm" />
            </div>
            <div class="ml-3 min-w-0 flex-1">
              <p class="text-sm font-semibold text-brand-blue truncate">{{ userName }}</p>
              <p class="text-xs text-gray-500 truncate">{{ userEmail }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Custom Footer -->
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface NavigationItem {
  label: string
  path: string
  icon?: any
}

interface Props {
  isOpen?: boolean
  title?: string
  navigationItems?: NavigationItem[]
  showUserProfile?: boolean
  showLogoutButton?: boolean
  userName?: string
  userEmail?: string
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  title: 'zC-Sign',
  navigationItems: () => [],
  showUserProfile: false,
  showLogoutButton: true,
  userName: '',
  userEmail: ''
})

const emit = defineEmits<{
  'update:isOpen': [value: boolean]
  'logout': []
}>()

const closeSidebar = () => {
  emit('update:isOpen', false)
}

const handleLogout = () => {
  emit('logout')
}

const userInitials = computed(() => {
  if (!props.userName) return 'U'
  return props.userName
    .split(' ')
    .map(name => name.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2)
})
</script> 